import { useMemo, useState, type JSX } from "react";
import {
  useCreateAllowedServiceAccountForOrganization,
  useDeleteAllowedServiceAccountForOrganization,
  useGetAllowedServiceAccountsForOrganization,
} from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { SkeletonLoader, Spinner } from "@via/components/loading/loading";
import { GetUserOrganizationDetailsForAdminResponseBodyItem } from "common";
import { EmptyDataIcon } from "@via/utils/custom-icons.tsx";

export default function ServiceAccountsPanel({
  organization,
}: {
  organization: GetUserOrganizationDetailsForAdminResponseBodyItem;
}): JSX.Element {
  const organizationId = organization?.id ?? "";

  const { data: allowed = [], isLoading } =
    useGetAllowedServiceAccountsForOrganization(
      { organizationId },
      !!organizationId
    );

  const { mutateAsync: createAllowed, isPending: isCreating } =
    useCreateAllowedServiceAccountForOrganization();
  const { mutateAsync: deleteAllowed, isPending: isDeleting } =
    useDeleteAllowedServiceAccountForOrganization();

  const [email, setEmail] = useState("");
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const canAdd = useMemo(() => {
    return !!organizationId && email.trim().length > 0 && !isCreating;
  }, [email, isCreating, organizationId]);

  const onAdd = async () => {
    if (!canAdd) return;
    await createAllowed({ organizationId, serviceAccountEmail: email.trim() });
    setEmail("");
  };

  const onDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await deleteAllowed({ organizationId, allowedServiceAccountId: id });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div data-testid="service-accounts-panel" className="space-y-6">
      <div className="text-sm text-gray-700">
        Configure which GCP service account emails can be used by organization
        admins when creating organization-level impersonation credentials.
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-900">
            Service account email
          </label>
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="service-account@project.iam.gserviceaccount.com"
            className="mt-2 block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => void onAdd()}
          disabled={!canAdd}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
          <Spinner loading={isCreating}>Add</Spinner>
        </button>
      </div>

      <div className="rounded-md border border-gray-200">
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-900">
          Approved emails
        </div>
        {isLoading ? (
          <div className="p-4">
            <SkeletonLoader count={1} />
          </div>
        ) : allowed.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 p-6 text-sm text-gray-600">
            <EmptyDataIcon />
            <div>
              No approved service accounts configured for this organization.
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {allowed.map(row => (
              <li
                key={row.id}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm">
                <div className="min-w-0 flex-1 truncate text-gray-900">
                  {row.serviceAccountEmail}
                </div>
                <button
                  type="button"
                  onClick={() => void onDelete(row.id)}
                  disabled={isDeleting && deletingId === row.id}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50">
                  <Spinner loading={isDeleting && deletingId === row.id}>
                    Remove
                  </Spinner>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
